/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * useLogOut — Session 302.
 *
 * Wires the header's Log Out control to the existing authClient.signOut(),
 * which was fully implemented but had no UI caller anywhere in the codebase.
 *
 * Deliberately NOT built as a header-driven endpoint call of its own — see
 * "The unauthenticated forum-logout endpoint" in the Session 301 handoff.
 * signOut() already does the right thing: it clears the local session token
 * only after the server confirms the session was deleted, and clears the
 * in-memory JWT cache immediately so a following visitor is never served the
 * previous customer's access token.
 *
 * A full-page navigation after a successful sign-out (rather than a local
 * state flip) is deliberate: every surface that reads auth state — Header,
 * MobileNavbar, the Deskshare Support widget — reads it fresh on mount from
 * authClient.isAuthenticated(), which is not itself reactive. A single
 * navigation keeps every one of them consistent without needing a second,
 * parallel state mechanism.
 */

import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import { authClient } from "@/lib/auth-client";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import { afterSignOutUrl } from "@/utils/signOutDestination";

export interface UseLogOutReturn {
  handleLogOut: () => void;
  isLoggingOut: boolean;
}

export function useLogOut(): UseLogOutReturn {
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogOut = useCallback(() => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);

    void authClient
      .signOut()
      .then((result) => {
        if (!result.error) {
          // VM-1 / N-3 (Session 484): the home page of the host the customer
          // is on, as an absolute address, passed through the shop's
          // sign-out page where that page is live (afterSignOutUrl). Never
          // configuration.websiteUrl: on a feature or test host that can be
          // the production site.
          window.location.href = afterSignOutUrl(
            new URL(AddLangToPath("/"), window.location.origin).href,
          );
          return;
        }
        // Section 7.6 of the Session 301 handoff: a failed sign-out must
        // leave the button usable for a retry rather than appearing to do
        // nothing. Report it so a broken sign-out cannot go unnoticed.
        //
        // A-21: the state here is PARTIAL, not "nothing happened". signOut()
        // calls browser-logout first, so by the time this branch runs the
        // Django session and the forum cookie are already cleared -- only the
        // account session survived. The message says so, because a report
        // reading "sign-out failed" sends whoever picks it up looking for a
        // customer who is still fully signed in, and they are not.
        setIsLoggingOut(false);
        // Session 383 A-1: through the module. This read `window.Sentry?.…`,
        // which this application never sets, so a partially failed sign-out --
        // the customer left signed in to the account session on a machine they
        // believe they have left -- was never reported.
        Sentry.captureMessage(
          "Sign-out partially failed: forum and browser sessions were cleared, " +
            `but the account session DELETE failed: ${result.error.message}`,
          "error",
        );
      })
      .catch(() => {
        setIsLoggingOut(false);
      });
  }, [isLoggingOut]);

  return { handleLogOut, isLoggingOut };
}
